import type { ReactNode } from 'react';
import type { Experiment } from 'types';
import type { SignificanceDisplayItem } from '@/state/experiments/useExperimentStats';
import type { StackedField } from '../../../ConsolePanel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { IconInfoCircle } from '@tabler/icons-react';
import { cn } from 'helpers/cn';
import { formatPercentage } from 'helpers/formatters';
import { GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, StackedRow } from '../../../ConsolePanel';
import { SignificanceExplainer } from './SignificanceExplainer';
import { MetricLabel } from './MetricLabel';

/**
 * Typed rather than `any`. The manager being untyped is what let the significance
 * read-out look up significanceData[currency][goal].frequentist[correction] against
 * a payload that had no `frequentist` level, and fail silently at runtime instead of
 * at compile time.
 */
type StatsManager = ReturnType<typeof import('@/state/experiments/useExperimentStats').useExperimentStats>;

type SignificanceTableProps = { experiment: Experiment; manager: StatsManager };

/**
 * Grid rather than `<table>`, so the name column can be `minmax(180px, 1fr)`: it takes
 * whatever width is spare and stops collapsing onto two lines when a reading column is
 * added. The whole grid has a min width and scrolls horizontally inside the card rather
 * than squeezing the numbers.
 */
const CHIP_CLASS = 'rounded-pill border px-2 py-0.5 font-mono text-nano uppercase tracking-[0.1em]';

/** The verdict chip is a conclusion, not a footnote, so it reads a size up from the rest. */
const VERDICT_CHIP_CLASS =
  'rounded-pill border px-2 py-0.5 font-mono text-micro font-medium uppercase tracking-[0.1em]';

/**
 * Two weights of cell, and which one a column gets is the whole point of the table.
 *
 * `RESULT` is what the reader came for — the interval, the p-value, the verdict —
 * and `SUPPORT` is the arithmetic that got there. Before this they were the same
 * 12px `--text`, so the conclusion had to be hunted for among its own inputs.
 * Both stay `num`, so every column is still tabular and still aligns.
 */
const RESULT_CELL = 'text-body font-medium';
const SUPPORT_CELL = 'text-dim';

type CellProps = { children: ReactNode; className?: string; title?: string };

const Cell = ({ children, className, title }: CellProps) => (
  <div role="cell" title={title} className={cn('num text-foreground', className)}>
    {children}
  </div>
);

const Head = ({ children, className }: CellProps) => (
  <div role="columnheader" className={cn('micro-label', className)}>
    {children}
  </div>
);

export const SignificanceTable = ({ experiment, manager }: SignificanceTableProps) => {
  const { significance, goal, evaluationMethod } = manager;

  // The Beta-Binomial model describes a converted / did-not-convert process, so
  // it has no revenue analogue. Say that rather than rendering nothing.
  if (evaluationMethod === 'bayesian' && goal === 'Revenue') {
    return (
      <div className="border-line bg-panel2 rounded-card border px-4 py-4">
        <h3 className="text-foreground mb-1 text-title font-medium tracking-[-0.01em]">Significance</h3>
        <p className="supporting max-w-3xl">
          Bayesian analysis is available for conversion goals only. It models each variant as a converted/not-converted
          outcome, which has no direct revenue equivalent. Switch the evaluation method to Frequentist to assess
          revenue, or switch the goal to Conversions.
        </p>
      </div>
    );
  }

  if (!significance || significance.length === 0) return null;

  const runningFor = (Date.now() - experiment.startsAt!) / 1000 / 60 / 60 / 24;
  const isFrequentist = evaluationMethod === 'frequentist';
  const showShortRunWarning = isFrequentist && !!significance.find((item) => item.significant) && runningFor < 14;

  // The interval columns carry 13px mono rather than 12px now, so their floors go up
  // with them — the point of a floor is that the number never wraps.
  const readingColumns = isFrequentist
    ? [
        'minmax(96px,auto)',
        'minmax(104px,auto)',
        'minmax(176px,auto)',
        ...(goal === 'Conversions' ? ['minmax(170px,auto)'] : []),
        'minmax(150px,auto)',
      ]
    : ['minmax(120px,auto)', 'minmax(130px,auto)', 'minmax(196px,auto)', 'minmax(196px,auto)'];

  const template = [
    '72px',
    'minmax(180px,1fr)',
    ...readingColumns,
    ...(showShortRunWarning ? ['minmax(200px,auto)'] : []),
  ].join(' ');

  /**
   * Derived once, rendered twice — as a grid row on a desktop and as a stack of
   * label/value pairs on a phone. Two renderers reading one array is what stops the
   * narrow shape from drifting away from the wide one.
   */
  const readings = significance.map((item) => ({
    item,
    leads: leadsTheField(item, isFrequentist),
    fields: isFrequentist ? frequentistFields({ item, goal, showShortRunWarning }) : bayesianFields(item),
  }));

  // Every row carries the same columns, so the first one's labels are the head row.
  const headFields = readings[0]!.fields;

  return (
    <TooltipProvider>
      <div className="border-line bg-panel2 rounded-card border">
        <div className="border-line border-b px-4 py-3">
          <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">Significance</h3>
          <p className="supporting">
            {isFrequentist
              ? "Determine the statistical relevance of your experiment's results."
              : 'Bayesian probability analysis of variant performance.'}
          </p>
        </div>

        {/* Below `md` every row restacks. The grid's floor is 900px, so on a phone the
            reader would have to swipe the variant name off screen to reach the p-value,
            and a verdict detached from the variant it judges is worse than no verdict. */}
        <div className="flex flex-col gap-2 p-3 md:hidden">
          {readings.map(({ item, fields, leads }) => (
            <StackedRow
              key={item.id}
              className={leads ? 'border-l-ok' : undefined}
              heading={
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="text-foreground min-w-0 truncate text-body" title={item.name}>
                    {item.name}
                  </span>
                  <span className="num text-dim shrink-0">#{item.id}</span>
                </div>
              }
              fields={fields}
            />
          ))}
        </div>

        <GridTable label="Significance" minWidth={900} template={template}>
          {/* Heads come off the same field list the cells do, so a column cannot end
              up labelled one thing on a desktop and another on a phone. */}
          <div
            role="row"
            className={cn(GRID_TABLE_ROW, 'border-b-line border-l-2 border-l-transparent items-center border-b py-2')}
          >
            <Head>ID</Head>
            <Head>Name</Head>
            {headFields.map((field) => (
              <Head key={field.key}>{field.label}</Head>
            ))}
          </div>

          <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
            {readings.map(({ item, fields, leads }) => (
              <div
                key={item.id}
                role="row"
                className={cn(
                  GRID_TABLE_ROW,
                  'border-b-line hover:bg-hover items-center border-b py-2.5',
                  'transition-colors duration-150 last:border-b-0',
                  // Every row carries the same 2px left border, transparent when the
                  // row is not the leader. A border that appeared on one row only
                  // would shunt that row 2px out of line with the head.
                  'border-l-2',
                  leads ? 'border-l-ok' : 'border-l-transparent',
                )}
              >
                <Cell className={SUPPORT_CELL}>{item.id}</Cell>
                <Cell className="truncate font-sans text-body" title={item.name}>
                  {item.name}
                </Cell>
                {fields.map((field) => (
                  <Cell key={field.key} className={field.className}>
                    {field.value}
                  </Cell>
                ))}
              </div>
            ))}
          </div>
        </GridTable>

        {/* The table states the numbers; this states what they mean and what to do. */}
        <div className="px-4 pb-4">
          <SignificanceExplainer significance={significance} evaluationMethod={evaluationMethod} />
        </div>
      </div>
    </TooltipProvider>
  );
};

type FrequentistFieldsParams = {
  item: SignificanceDisplayItem;
  goal: string;
  showShortRunWarning: boolean;
};

/**
 * The row that carries the conclusion, so it can be found without reading every
 * number. Frequentist: the verdict column already says "Significant". Bayesian:
 * there is no verdict column, so the 90% probability-of-best threshold that
 * `probabilityTone` already treats as decisive is reused rather than invented.
 */
const leadsTheField = (item: SignificanceDisplayItem, isFrequentist: boolean) =>
  isFrequentist ? !!item.significant : (item.probabilityOfBeingBest ?? 0) >= 0.9;

/** An interval that spans zero has not decided anything, so it takes warn, not danger. */
const liftTone = (interval: { lower: number; upper: number } | null | undefined) => {
  if (interval && interval.lower > 0) return 'text-ok';
  if (interval && interval.upper < 0) return 'text-danger';
  return 'text-warn';
};

const frequentistFields = ({ item, goal, showShortRunWarning }: FrequentistFieldsParams): StackedField[] => {
  const SPRT_RESULTS = ['Variant significant', 'Variant shows no effect', 'Continue experiment'];

  const sprtTone =
    item.sprtResult === SPRT_RESULTS[0]
      ? 'border-ok-line text-ok'
      : item.sprtResult === SPRT_RESULTS[1]
        ? 'border-line text-dim'
        : 'border-accent-line text-accent';

  // "No significance" is the ordinary state of a running experiment rather than a
  // failure, so it reads neutral; only a positive verdict takes a colour.
  const significantTone = item.significant ? 'border-ok-line text-ok' : 'border-line text-dim';

  const liftInterval = item.liftInterval;

  return [
    {
      key: 'zScore',
      label: (
        <MetricLabel metric="zScore" className="micro-label">
          z-score
        </MetricLabel>
      ),
      // z-score is an input to the verdict, not the verdict — it recedes.
      className: SUPPORT_CELL,
      value: (
        <span className="inline-flex items-center gap-1">
          {item.zScore?.toFixed(4) || '-'}
          {item.warning && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconInfoCircle className="text-warn size-3.5" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{item.warning}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      ),
    },
    {
      key: 'pValue',
      label: (
        <MetricLabel metric="pValue" className="micro-label">
          p-value
        </MetricLabel>
      ),
      className: RESULT_CELL,
      // The payload rounds to 4dp, so a stored 0.0000 means "below 0.00005", not zero:
      // a p-value of exactly 0 is not a thing a z-test can produce.
      value: item.pValue == null ? '-' : item.pValue < 0.0001 ? '< 0.0001' : item.pValue.toFixed(4),
    },
    {
      key: 'liftCi',
      label: (
        <MetricLabel metric="liftCi" className="micro-label">
          Lift CI
        </MetricLabel>
      ),
      className: cn(RESULT_CELL, liftTone(liftInterval)),
      value: liftInterval
        ? `${formatPercentage(liftInterval.lower * 100)} to ${formatPercentage(liftInterval.upper * 100)}`
        : '-',
    },
    ...(goal === 'Conversions'
      ? [
          {
            key: 'sprt',
            label: (
              <MetricLabel metric="sprt" className="micro-label">
                SPRT
              </MetricLabel>
            ),
            value: <span className={cn(CHIP_CLASS, sprtTone)}>{item.sprtResult || '-'}</span>,
          },
        ]
      : []),
    {
      key: 'significant',
      label: (
        <MetricLabel metric="significant" className="micro-label">
          Significant
        </MetricLabel>
      ),
      value: (
        <span className={cn(VERDICT_CHIP_CLASS, significantTone)}>{item.significant ? 'Significant' : 'None'}</span>
      ),
    },
    ...(showShortRunWarning
      ? [
          {
            key: 'shortRun',
            label: 'Info',
            className: 'text-warn',
            value: item.significant ? 'Short run time — results may be unreliable' : '',
          },
        ]
      : []),
  ];
};

const bayesianFields = (item: SignificanceDisplayItem): StackedField[] => {
  const probBest = item.probabilityOfBeingBest ?? 0;
  const probTone =
    probBest >= 0.9
      ? 'border-ok-line text-ok'
      : probBest >= 0.6
        ? 'border-warn-line text-warn'
        : 'border-line text-dim';

  const liftInterval = item.liftInterval;

  return [
    {
      key: 'probabilityBest',
      label: (
        <MetricLabel metric="probabilityBest" className="micro-label">
          Prob. best
        </MetricLabel>
      ),
      // Probability-of-best is the Bayesian verdict, so it takes the verdict weight.
      value: <span className={cn(VERDICT_CHIP_CLASS, probTone)}>{formatPercentage(probBest * 100)}</span>,
    },
    {
      key: 'expectedLoss',
      label: (
        <MetricLabel metric="expectedLoss" className="micro-label">
          Expected loss
        </MetricLabel>
      ),
      className: SUPPORT_CELL,
      value: formatPercentage((item.expectedLoss ?? 0) * 100),
    },
    {
      key: 'credibleInterval',
      label: (
        <MetricLabel metric="credibleInterval" className="micro-label">
          Credible interval
        </MetricLabel>
      ),
      className: RESULT_CELL,
      value: item.credibleInterval
        ? `${formatPercentage(item.credibleInterval.lower * 100)} to ${formatPercentage(item.credibleInterval.upper * 100)}`
        : '-',
    },
    {
      key: 'liftCi',
      label: (
        <MetricLabel metric="liftCi" className="micro-label">
          Lift CI
        </MetricLabel>
      ),
      className: cn(RESULT_CELL, liftTone(liftInterval)),
      value: liftInterval
        ? `${formatPercentage(liftInterval.lower * 100)} to ${formatPercentage(liftInterval.upper * 100)}`
        : '-',
    },
  ];
};
